/**
 * @file care-agent-run.schema.ts
 * @description Execução do agente de jornadas (`careAgentRuns`) e seus
 * eventos (`careAgentRuns/{id}/eventos`). Plano "Agente de Jornadas", F2.
 *
 * O REGISTRO É O QUE TORNA A EXECUÇÃO RECONSTRUÍVEL: quem pediu, em qual
 * marca, com qual modelo e qual versão das instruções, cada chamada de
 * ferramenta em ordem, o consumo, e a proposta como o agente entregou.
 *
 * O DOCUMENTO ENVIADO NÃO É GUARDADO (decisão de 25/09/2026): vive só durante
 * a execução. Fica o suficiente para reconhecer o mesmo arquivo depois —
 * nome, tamanho e hash. A proposta, que é o que importa, fica salva.
 *
 * Os eventos são o mesmo fluxo que anima a tela: cada passo é gravado antes
 * de ser transmitido, então a tela que reconecta reproduz tudo a partir do
 * último evento que viu.
 */

import { z } from 'zod';

export const CareAgentRunStatusEnum = z.enum([
  /** O agente está trabalhando. */
  'EXECUTANDO',
  /** Proposta entregue, com pendências para a pessoa resolver. */
  'AGUARDANDO_PENDENCIAS',
  /** Proposta entregue sem pendências — pode ir ao importador. */
  'PRONTA',
  /** O pedido não era criar jornada; a pessoa viu a mensagem fixa. */
  'FORA_DO_ESCOPO',
  /** Terminou com erro (modelo, limite de passos, proposta inválida). */
  'FALHOU',
  /** Parou de dar sinal de vida no meio (conexão ou instância encerrada). */
  'INTERROMPIDA',
]);
export type CareAgentRunStatus = z.infer<typeof CareAgentRunStatusEnum>;

export const STATUS_FINAIS: readonly CareAgentRunStatus[] = ['AGUARDANDO_PENDENCIAS', 'PRONTA', 'FORA_DO_ESCOPO', 'FALHOU', 'INTERROMPIDA'];

export const CareAgentModeloSchema = z.object({
  provedor: z.enum(['VERTEX_GEMINI', 'VERTEX_CLAUDE']),
  /** Identificador exato, como o provedor o chama. Ex.: `gemini-3.8-flash`. */
  id: z.string().min(1),
  /** Local do endpoint no Vertex AI. Ex.: `global`. */
  local: z.string().min(1),
});
export type CareAgentModelo = z.infer<typeof CareAgentModeloSchema>;

export const CareAgentRunSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string(),
  criadoPor: z.string(),
  status: CareAgentRunStatusEnum,
  entrada: z.object({
    tipo: z.enum(['DOCUMENTO', 'PROMPT']),
    nomeArquivo: z.string().nullable().default(null),
    tamanhoBytes: z.number().int().nonnegative(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
  }),
  modelo: CareAgentModeloSchema,
  versaoDasInstrucoes: z.string(),
  consumo: z
    .object({
      tokensEntrada: z.number().int().nonnegative().default(0),
      tokensSaida: z.number().int().nonnegative().default(0),
      chamadasAoModelo: z.number().int().nonnegative().default(0),
      chamadasDeFerramenta: z.number().int().nonnegative().default(0),
      duracaoMs: z.number().int().nonnegative().default(0),
    })
    .default({}),
  /** A proposta como o agente entregou (já com ids e origem do servidor). */
  proposta: z.record(z.unknown()).nullable().default(null),
  /** Classificação da recusa, quando FORA_DO_ESCOPO. O texto mostrado é fixo. */
  motivoDaRecusa: z.string().nullable().default(null),
  erro: z.string().nullable().default(null),
  /** Último sinal de vida — é por ele que uma execução parada vira INTERROMPIDA. */
  batimentoEm: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type CareAgentRun = z.infer<typeof CareAgentRunSchema>;

export const CareAgentEventoTipoEnum = z.enum([
  'INICIO',
  'STATUS',
  'FERRAMENTA_INICIO',
  'FERRAMENTA_FIM',
  'PROPOSTA_REJEITADA',
  'PROPOSTA',
  'RECUSA',
  'ERRO',
  'FIM',
]);
export type CareAgentEventoTipo = z.infer<typeof CareAgentEventoTipoEnum>;

export const CareAgentEventoSchema = z.object({
  /** Ordem dentro da execução, começando em 1. */
  seq: z.number().int().positive(),
  tipo: CareAgentEventoTipoEnum,
  /** Texto curto para a tela ("Consultando o catálogo da marca"). */
  mensagem: z.string().nullable().default(null),
  /** Detalhe estruturado: nome da ferramenta, argumentos, resumo do resultado. */
  dados: z.record(z.unknown()).default({}),
  em: z.date(),
});
export type CareAgentEvento = z.infer<typeof CareAgentEventoSchema>;
